import React, { useState, useContext } from "react";
import { motion } from "framer-motion";
import { Mail, Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

import emailService from "../../services/emailService";
import AuthContext from "../../context/AuthContext";
import { useTranslation } from "../../i18n";

const EmailDiagnosticsSection = () => {
  const { user } = useContext(AuthContext);
  const { t } = useTranslation("admin_integrations");
  const [emailTo, setEmailTo] = useState(user?.email || "");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSend = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);

    if (!isValidEmail(emailTo)) {
      setError(t("email_diagnostics.invalid_email"));
      return;
    }

    setSending(true);
    try {
      const data = await emailService.sendTestEmail(emailTo.trim());
      setResult(data);
    } catch (err) {
      setError(err.message || t("email_diagnostics.send_failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 sm:p-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <div className="org-gradient text-white p-2.5 rounded-xl">
          <Mail className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{t("email_diagnostics.title")}</h2>
          <p className="text-sm text-gray-500">
            {t("email_diagnostics.subtitle")}
          </p>
        </div>
      </div>

      <div className="mt-6 max-w-xl">
        <form onSubmit={handleSend} className="space-y-4">
          <div>
            <label
              htmlFor="email-to"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              {t("email_diagnostics.recipient")}
            </label>
            <input
              id="email-to"
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
              autoComplete="off"
            />
            <p className="mt-1.5 text-xs text-gray-400">
              {t("email_diagnostics.provider_hint")}
            </p>
          </div>

          <button
            type="submit"
            disabled={sending}
            className="inline-flex items-center gap-2 org-gradient text-white font-medium px-5 py-2.5 rounded-full shadow-md hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t("email_diagnostics.sending")}
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                {t("email_diagnostics.send")}
              </>
            )}
          </button>
        </form>

        {/* Success result */}
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-5 rounded-xl border border-green-200 bg-green-50 p-4"
          >
            <div className="flex items-start gap-2.5">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
              <div className="text-sm text-green-800">
                <p className="font-semibold">{t("email_diagnostics.success")}</p>
                <dl className="mt-2 space-y-1">
                  <div className="flex gap-2">
                    <dt className="text-green-700/80">{t("email_diagnostics.provider")}</dt>
                    <dd className="font-medium uppercase">{result.provider}</dd>
                  </div>
                  {result.from_email && (
                    <div className="flex gap-2">
                      <dt className="text-green-700/80">{t("email_diagnostics.from")}</dt>
                      <dd className="font-medium">{result.from_email}</dd>
                    </div>
                  )}
                  {result.message_id && (
                    <div className="flex gap-2">
                      <dt className="text-green-700/80">{t("email_diagnostics.message_id")}</dt>
                      <dd className="font-mono text-xs break-all">
                        {result.message_id}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          </motion.div>
        )}

        {/* Error result */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4"
          >
            <div className="flex items-start gap-2.5">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
              <div className="text-sm text-red-800">
                <p className="font-semibold">{t("email_diagnostics.failed")}</p>
                <p className="mt-1 break-words">{error}</p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default EmailDiagnosticsSection;
